import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, ArrowLeft, ShieldCheck, Truck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/zafaraApi";
import { useShop } from "@/context/ShopContext";
import { AppPromoCard } from "@/components/shop/AppPromoCard";

const formatXOF = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Number(n) || 0) + " F";

const PAYMENT_OPTIONS = [
  { value: "momo_mtn", label: "Mobile Money — MTN" },
  { value: "momo_moov", label: "Mobile Money — Moov" },
  { value: "cash", label: "Paiement à la livraison" },
];

export default function Checkout() {
  const { user, cart, refreshCart } = useShop();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    address: "",
    city: "",
    quartier: "",
    phone: "",
    payment_method: "momo_mtn",
  });
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null); // order response

  useEffect(() => {
    if (!user) { navigate("/compte/connexion?next=/commande"); return; }
    (async () => {
      try {
        const c = await api.cities();
        setCities(c?.cities || []);
      } catch { /* ignore */ }
    })();
    // Prefill from user profile
    setForm((f) => ({
      ...f,
      city: f.city || user?.pickup_city || "",
      quartier: f.quartier || user?.pickup_quartier || "",
      phone: f.phone || user?.phone || "",
    }));
  }, [user, navigate]);

  const selectedCity = cities.find((c) => c.name === form.city);
  const quartierList = selectedCity?.quartiers || [];
  const deliveryFee = selectedCity?.fee || 0;
  const subtotal = (cart.items || []).reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0);
  const grandTotal = subtotal + deliveryFee;

  const canSubmit = form.address && form.city && form.phone && (cart.items || []).length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || placing) return;
    setPlacing(true);
    try {
      const res = await api.orderCreate(form);
      // Zafara API may wrap the order under different keys — be tolerant.
      const order = res?.order || res?.data?.order || res?.data || res || {};
      const orderId = order.id || res?.order_id || res?.id || null;
      // API uses `delivery_code` as the customer-facing tracking code.
      const trackingCode = order.delivery_code || order.tracking_code || res?.delivery_code || res?.tracking_code || orderId;
      setPlaced({ ...order, id: orderId, delivery_code: trackingCode, tracking_code: trackingCode });
      toast.success("Commande enregistrée !");
      await refreshCart();
    } catch (err) {
      toast.error(err.message || "Impossible de créer la commande.");
    } finally {
      setPlacing(false);
    }
  };

  if (placed) {
    const code = placed.delivery_code || placed.tracking_code || placed.id;
    return (
      <section className="mx-auto max-w-2xl px-5 py-16 text-center">
        <CheckCircle2 className="h-16 w-16 mx-auto text-[#0E7A3B]" />
        <h1 data-testid="order-success-title" className="mt-6 font-display text-3xl md:text-4xl text-[#16241B]">
          Commande confirmée !
        </h1>
        <p className="mt-3 text-[#16241B]/70">Vous allez recevoir un SMS/WhatsApp avec votre code de suivi. Suivez ensuite votre livreur en direct sur la carte de l'app Zafara.</p>

        <div className="mt-8 rounded-3xl border border-[#EDE7D9] bg-white p-6">
          <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Code de suivi</p>
          <p data-testid="order-tracking-code" className="mt-2 font-display font-bold text-3xl text-[#0E7A3B] tracking-wider">
            {String(code).slice(0, 12).toUpperCase()}
          </p>
          <p className="mt-3 text-xs text-[#16241B]/60">Conservez-le pour suivre votre livraison à tout moment.</p>
        </div>

        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link to={`/suivi/${placed.id || code}`} className="rounded-full bg-[#0E7A3B] text-white px-6 py-3 font-semibold hover:bg-[#0d6a34] transition-colors">
            Suivre ma commande
          </Link>
          <Link to="/mes-commandes" className="rounded-full border border-[#EDE7D9] bg-white px-6 py-3 font-semibold hover:border-[#0E7A3B] transition-colors">
            Mes commandes
          </Link>
        </div>

        <div className="mt-10 text-left">
          <AppPromoCard />
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-10 md:py-16">
      <Helmet>
        <title>Finaliser la commande — Zafara Bénin</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Link to="/panier" className="inline-flex items-center gap-2 text-sm text-[#16241B]/70 hover:text-[#0E7A3B] mb-6">
        <ArrowLeft className="h-4 w-4" /> Retour au panier
      </Link>
      <h1 className="font-display text-4xl md:text-5xl text-[#16241B] tracking-tighter">
        Finaliser la <span className="text-[#0E7A3B]">commande</span>
      </h1>

      <form onSubmit={submit} className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border border-[#EDE7D9] bg-white p-6 md:p-8">
            <h2 className="font-display text-xl text-[#16241B] mb-6">Adresse de livraison</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="block md:col-span-2">
                <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Adresse complète *</span>
                <input
                  data-testid="checkout-address"
                  required
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Nom de rue, repère…"
                  className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
                />
              </label>
              <label className="block">
                <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Ville *</span>
                <select
                  data-testid="checkout-city"
                  required
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value, quartier: "" })}
                  className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
                >
                  <option value="">Sélectionner…</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.name}>{c.name} — livraison {formatXOF(c.fee)}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Quartier</span>
                <select
                  data-testid="checkout-quartier"
                  value={form.quartier}
                  onChange={(e) => setForm({ ...form, quartier: e.target.value })}
                  disabled={!quartierList.length}
                  className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] disabled:opacity-60"
                >
                  <option value="">{quartierList.length ? "Sélectionner…" : "Sélectionnez d'abord une ville"}</option>
                  {quartierList.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Téléphone / WhatsApp *</span>
                <input
                  data-testid="checkout-phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+229 01 XX XX XX XX"
                  className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
                />
              </label>
            </div>
          </div>

          <div className="rounded-3xl border border-[#EDE7D9] bg-white p-6 md:p-8">
            <h2 className="font-display text-xl text-[#16241B] mb-6">Méthode de paiement</h2>
            <div className="space-y-2">
              {PAYMENT_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 rounded-2xl border p-4 cursor-pointer transition-colors ${
                    form.payment_method === opt.value ? "border-[#0E7A3B] bg-[#0E7A3B]/5" : "border-[#EDE7D9] hover:border-[#0E7A3B]/50"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    data-testid={`checkout-pay-${opt.value}`}
                    value={opt.value}
                    checked={form.payment_method === opt.value}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                    className="accent-[#0E7A3B]"
                  />
                  <span className="text-sm font-medium text-[#16241B]">{opt.label}</span>
                </label>
              ))}
            </div>
            <p className="mt-4 flex items-center gap-2 text-xs text-[#16241B]/60">
              <ShieldCheck className="h-3.5 w-3.5 text-[#0E7A3B]" />
              Paiement traité par l'API Zafara — même flux que l'app mobile.
            </p>
          </div>
        </div>

        <aside className="rounded-3xl border border-[#EDE7D9] bg-white p-6 h-fit sticky top-24">
          <h2 className="font-display text-xl text-[#16241B]">Récapitulatif</h2>
          <div className="mt-4 space-y-2 text-sm max-h-64 overflow-auto">
            {(cart.items || []).map((it) => (
              <div key={it.id} className="flex justify-between gap-2">
                <span className="line-clamp-1 text-[#16241B]/80">{it.name} ×{it.quantity}</span>
                <span className="font-semibold shrink-0">{formatXOF((it.price || 0) * (it.quantity || 0))}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-[#EDE7D9] space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#16241B]/70">Sous-total</span>
              <span className="font-semibold">{formatXOF(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#16241B]/70 inline-flex items-center gap-1"><Truck className="h-3 w-3" /> Livraison</span>
              <span className="font-semibold">{formatXOF(deliveryFee)}</span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-[#EDE7D9] flex justify-between items-baseline">
            <span className="font-display text-lg">Total</span>
            <span data-testid="checkout-total" className="font-display font-bold text-2xl text-[#0E7A3B]">{formatXOF(grandTotal)}</span>
          </div>
          <button
            data-testid="checkout-submit"
            type="submit"
            disabled={!canSubmit || placing}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-6 py-3.5 font-display font-bold shadow-[0_15px_40px_rgba(245,168,0,0.35)] hover:bg-[#0E7A3B] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {placing ? "Envoi…" : "Confirmer la commande"}
          </button>
        </aside>
      </form>
    </section>
  );
}
